import React, { useEffect, useState } from 'react';
import { ClipboardList, Download, X, Plus, Send, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const prevMonth = () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 7); };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Recipients = ({ list, onChange }) => {
  const [v, setV] = useState('');
  const add = () => {
    const e = v.trim();
    if (!EMAIL_RE.test(e)) return toast({ title: 'Enter a valid email', variant: 'destructive' });
    if (!list.some((x) => x.toLowerCase() === e.toLowerCase())) onChange([...list, e]);
    setV('');
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {list.length === 0 && <p className="text-[11px] text-rose-600">No recipients — the email won't be sent.</p>}
        {list.map((e) => (
          <span key={e} className="inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 px-2 py-0.5 text-[11px] text-violet-800" data-testid={`audit-recipient-${e}`}>
            {e}
            <button type="button" onClick={() => onChange(list.filter((x) => x !== e))} data-testid={`audit-recipient-remove-${e}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} placeholder="add email…" className="h-8 text-xs" data-testid="audit-recipient-input" />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="audit-recipient-add"><Plus className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );
};

export const BalanceAuditMenu = () => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(prevMonth());
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => { if (open) api.get('/api/leave-audit/settings').then((r) => setCfg(r.data)).catch(() => {}); }, [open]);

  const download = async (format) => {
    setBusy(format);
    try {
      const [y, m] = month.split('-');
      const r = await api.get('/api/leave-audit/download', { params: { year: y, month: Number(m), format }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `leave_balance_audit_${y}_${m}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
    finally { setBusy(''); }
  };

  const save = async (next) => {
    setCfg(next);
    try { setCfg((await api.put('/api/leave-audit/settings', next)).data); toast({ title: 'Audit email settings saved' }); }
    catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const sendNow = async () => {
    setBusy('send');
    try { await api.post('/api/leave-audit/send-now'); toast({ title: 'Audit report sent', description: "Last month's report emailed to the recipients." }); }
    catch (e) { toast({ title: 'Send failed', description: e.response?.data?.detail || 'Email could not be sent', variant: 'destructive' }); }
    finally { setBusy(''); api.get('/api/leave-audit/settings').then((r) => setCfg(r.data)).catch(() => {}); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="balance-audit-btn">
          <ClipboardList className="h-4 w-4 mr-2" /> Balance Audit Report
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 space-y-4" align="end" data-testid="balance-audit-popover">
        <div>
          <p className="text-sm font-semibold text-gray-800">Leave Balance Audit</p>
          <p className="text-[11px] text-gray-500">Active staff at month end, plus leavers at their last working day, with year-by-year working.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600">Month</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9" data-testid="audit-month-input" />
          </div>
          <Button size="sm" variant="outline" onClick={() => download('pdf')} disabled={!!busy} data-testid="audit-download-pdf">
            {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} PDF
          </Button>
          <Button size="sm" onClick={() => download('excel')} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="audit-download-excel">
            {busy === 'excel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Excel
          </Button>
        </div>
        {cfg && (
          <div className="border-t pt-3 space-y-2" data-testid="audit-email-settings">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-700">Auto-email on the 1st (09:00 UAE)</p>
              <Switch checked={!!cfg.active} onCheckedChange={(v) => save({ ...cfg, active: v })} data-testid="audit-auto-toggle" />
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              Attach as
              <select value={cfg.format} onChange={(e) => save({ ...cfg, format: e.target.value })} className="border rounded px-1.5 py-0.5 bg-white" data-testid="audit-format-select">
                <option value="excel">Excel</option><option value="pdf">PDF</option>
              </select>
            </div>
            <Recipients list={cfg.recipients || []} onChange={(recipients) => save({ ...cfg, recipients })} />
            <div className="flex items-center justify-between text-[11px] text-gray-500">
              <span data-testid="audit-next-run">{cfg.active && cfg.next_run_at ? `Next: ${new Date(cfg.next_run_at).toLocaleString('en-GB')}` : 'Auto-email paused'}
                {cfg.last_status ? ` · last ${cfg.last_status}` : ''}</span>
              <Button size="sm" variant="ghost" onClick={sendNow} disabled={!!busy || !(cfg.recipients || []).length} data-testid="audit-send-now">
                {busy === 'send' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Send now
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
